import { X_AXIS_KEY } from "components/Graph/constants.ts";
import { AggregatedTableData, SECONDARY_AXIS_KEYS } from "components/Graph/types.ts";
import { GapFiller } from "components/Graph/utils/GapFiller.ts";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils.ts";

import MoneyData = App.Data.MoneyData;

export class TimeGraphUtils<TData extends object> {
    private readonly data: TData[];

    constructor(data: TData[]) {
        this.data = data;
    }

    public getGraphData<TPath extends keyof TData>(metrics: TPath[]) {
        const secondaryKeysLookup = new Set(SECONDARY_AXIS_KEYS);

        const accumulator: Record<
            "primary" | "secondary" | "merge" | "primaryKeys" | "secondaryKeys",
            { [key: string]: string | number }[]
        > = {
            primary: [],
            secondary: [],
            merge: [],
            primaryKeys: [],
            secondaryKeys: [],
        };

        for (const entry of this.data) {
            const newPrimary = {} as { [key: string]: string | number };
            const newSecondary = {} as { [key: string]: string | number };

            for (const [metric, rawValue] of Object.entries(entry)) {
                /* re-map date_at as datetime_at to keep it consistent... */
                if (metric === "date_at" || metric === X_AXIS_KEY) {
                    newPrimary[X_AXIS_KEY] = rawValue as string;
                    newSecondary[X_AXIS_KEY] = rawValue as string;
                    continue;
                }

                if (!metrics.includes(metric as TPath)) {
                    continue;
                }

                if (secondaryKeysLookup.has(metric)) {
                    newSecondary[metric] = GeneralGraphUtils.resolveMetricValueToNumber(rawValue);
                } else {
                    newPrimary[metric] = GeneralGraphUtils.resolveMetricValueToNumber(rawValue);
                }
            }

            accumulator.primary.push(newPrimary);
            accumulator.secondary.push(newSecondary);
            accumulator.merge.push({ ...newPrimary, ...newSecondary });
        }

        const primary = accumulator.primary.filter(GeneralGraphUtils.filterEmptyObjects);
        const secondary = accumulator.secondary.filter(GeneralGraphUtils.filterEmptyObjects);
        const merge = GapFiller.fillPerformanceGraphGaps(accumulator.merge, metrics as (string | number)[]);

        const primaryKeys =
            primary.length > 0
                ? [...new Set(primary.flatMap((item) => Object.keys(item)).filter((k) => k !== X_AXIS_KEY))]
                : [];

        const secondaryKeys =
            secondary.length > 0
                ? [...new Set(secondary.flatMap((item) => Object.keys(item)).filter((k) => k !== X_AXIS_KEY))]
                : [];

        return {
            primaryKeys,
            secondaryKeys,
            merge: merge.sort((a, b) => a[X_AXIS_KEY].toString().localeCompare(b[X_AXIS_KEY].toString())),
        };
    }

    public getHighlightsData<TPath extends keyof TData>(metrics: TPath[]) {
        return this.data.reduce((acc, curr) => {
            const entry: Record<string, number | string | MoneyData | null> = {};

            let hasData = false;
            for (const [metric, rawValue] of Object.entries(curr)) {
                if (!metrics.includes(metric as TPath)) {
                    continue;
                }

                const value = GeneralGraphUtils.resolveMetricValueToOrigin(rawValue);
                if (value !== null && value !== undefined) {
                    hasData = true;
                }

                entry[metric] = value as number | string | MoneyData;
            }

            if (hasData) {
                acc.push(entry as AggregatedTableData<TData>);
            }

            return acc;
        }, [] as AggregatedTableData<TData>[]);
    }
}
